/* ==========================================================================
   SIMPS — Free Pump Reconciler
   --------------------------------------------------------------------------
   Buy 6 one-time bottle-equivalents -> ONE free Syrup Pump.
   A case counts as its pack size (12), but the offer is capped at ONE pump
   per order: 6 bottles, 12 bottles, 1 case and 2 cases all earn exactly one.
   Cases also get free shipping, handled by a separate shipping discount.

   WHY THERE IS NO DISCOUNT INVOLVED
   ---------------------------------
   The first version used a native Shopify Buy X Get Y discount. That cannot
   work on this store: the "3 for $33" automatic discount covers the same syrup
   and soda collections that qualify for the pump, and Shopify permits only ONE
   product discount per line item. The syrups cannot simultaneously be
   discounted by "3 for $33" and act as the BXGY prerequisite, so Shopify picks
   whichever saves more — always "3 for $33" — and the pump was silently charged
   at full price. No combination setting fixes that; it is structural.

   So the pump is a genuinely $0.00 product instead:
     "Free Syrup Pump" — variant 54236366274833, $0.00, status UNLISTED
     (direct link only; never in search, collections or recommendations).
     Inventory is TRACKED with oversell allowed, so every giveaway is counted
     and a zero balance can never block a checkout.
   Nothing can compete with a price that is already zero.

   COUNTING RULES (the subtle part)
   --------------------------------
   - Counts QUANTITY, not line items.
   - ONE-TIME ONLY. `selling_plan_allocation` is judged per LINE, because the
     same bottle is sold both one-time and on subscription.
   - CASES count as their pack size, read from "(Case of N)" in the title rather
     than hardcoded, so a future Case of 24 works with no code change.
   - ZERO-PRICED LINES ARE IGNORED. Once Recharge bundles are activated they
     expand into real component line items priced at $0.00 — a Case of 12 Cola
     becomes "1 case + 12 Cola at $0.00". Those components carry eligible
     product types, which made a Signature 12 hand out free pumps on top of the
     12 pumps already in the box. Ignoring zero-priced lines removes every
     bundle component at a stroke, while cases still count because the CASE
     line itself is $132 and is matched before that check runs.
   - THE SWAP: free pumps cover the customer's own paid pumps first, so nobody
     pays for a pump they earned. The swapped count lives in a cart attribute
     so dropping back below the threshold hands their paid pump back.
   - Deleting the free pump is respected — we stand down for the rest of the
     session rather than re-adding it on every render.

   This theme dispatches no cart events and exposes no global `halo`, so cart
   mutations are detected by wrapping fetch + XMLHttpRequest, and the UI is
   refreshed by calling the cart custom elements' own `.update(cart)` methods.
   ========================================================================== */

(function () {
  'use strict';

  var CONFIG = {
    paidPumpVariantId: 51245823492369,      // "Syrup Pumps" — $6.49, customer-added
    freePumpVariantId: 54236366274833,      // "Free Syrup Pump" — $0.00, UNLISTED, added by us
    threshold: 6,                            // bottle-equivalents per free pump

    // Product types that count as a bottle. Cases are handled separately via
    // the title pack size, because a case product has no type of its own.
    eligibleTypes: ['Syrups', 'Soda Concentrate', 'Caesar Mix', 'Concentrate'],

    freeFlag: '_free_pump',

    // Hard cap on free pumps per order. SIMPS decided 2026-08-17 that the
    // offer is ONE pump, full stop: 6 bottles, 12 bottles, 1 case and 2 cases
    // all earn exactly one. Cases additionally come with free shipping, which
    // is handled by a separate shipping discount, not by this script.
    // Set to 0 to go back to uncapped (one per `threshold` units).
    maxFreePumps: 1,

    debounceMs: 400,
    debug: false
  };

  var SENTINEL = 'X-Free-Pump-Internal';
  var DISMISS_KEY = 'simps:freePumpDismissedAt';
  var SWAP_ATTR = '_free_pump_swapped';

  var busy = false;
  var queued = false;
  var timer = null;
  var wasRemovedByCustomer = false;

  function log() {
    if (!CONFIG.debug) return;
    try { console.log.apply(console, ['[free-pump]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* ---------------------------------------------------------------- helpers */

  function cartUrl(path) {
    var root = (window.routes && window.routes.root) || '';
    if (root && root.charAt(root.length - 1) === '/') root = root.slice(0, -1);
    return root + path;
  }

  function props(item) {
    return item && item.properties ? item.properties : {};
  }

  // A line the customer bought as a subscription. Judged per line: the same
  // bottle is sold both ways, so product-level checks would be wrong.
  function isSubscriptionLine(item) {
    return !!item.selling_plan_allocation;
  }

  // A child line of a Recharge bundle, when Recharge stamps its properties.
  function isBundleLine(item) {
    var p = props(item);
    return !!(p._rc_bundle || p._rc_bundle_parent || p._rc_bundle_variant);
  }

  // Belt-and-braces guard. Wholesale SKUs share productType "Syrups" but are
  // all DRAFT and unpurchasable on the storefront; this catches the case where
  // one is ever activated by mistake.
  function isWholesaleLine(item) {
    var title = item.product_title || item.title || '';
    return /(^|\s)WS\s|\bBIB\b|12x946/i.test(title);
  }

  // A case is one line item that represents N bottles. The pack size is read
  // from the title rather than assumed, so a future "(Case of 24)" works with
  // no code change. The case product itself has no eligible product type, so
  // this check must run BEFORE the type check.
  function casePackSize(item) {
    var title = item.product_title || item.title || '';
    var m = /\(Case of (\d+)\)/i.exec(title);
    return m ? parseInt(m[1], 10) : 0;
  }

  // A line the customer is not paying for.
  //
  // THIS IS THE SIGNATURE-BUNDLE FIX. Once Recharge bundles are activated they
  // expand into real component line items priced at zero — a Case of 12 Cola
  // becomes "1 case + 12 Cola at $0.00". Those components have eligible product
  // types, so counting them made a Signature 12 hand out free pumps on top of
  // the 12 pumps already in the box.
  //
  // Components ride along at zero; a bottle someone actually bought never does.
  // So ignoring zero-priced lines removes every bundle component at a stroke.
  // Cases still count, because the CASE line itself is $132 and is matched by
  // casePackSize() above before we ever get here.
  function isFreeRider(item) {
    return item.final_line_price === 0 || item.final_price === 0;
  }

  function isEligibleLine(item) {
    if (CONFIG.eligibleTypes.indexOf(item.product_type) === -1) return false;
    if (isSubscriptionLine(item)) return false;
    if (isBundleLine(item)) return false;
    if (isFreeRider(item)) return false;
    if (isWholesaleLine(item)) return false;
    return true;
  }

  // Total bottle-equivalents in the cart that count toward the offer.
  function eligibleUnits(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];

      // One-time purchases only, and never our own free pump.
      if (isSubscriptionLine(item) || isFreePumpLine(item)) continue;

      // A case counts as its pack size. Checked first: the case product has no
      // eligible type of its own, and its components are zero-priced.
      var pack = casePackSize(item);
      if (pack > 0) {
        if (!isWholesaleLine(item)) total += item.quantity * pack;
        continue;
      }

      if (isEligibleLine(item)) total += item.quantity;
    }
    return total;
  }

  // A pump the CUSTOMER put in the cart and is paying for.
  function isPaidPumpLine(item) {
    return item.variant_id === CONFIG.paidPumpVariantId && !isSubscriptionLine(item);
  }

  // The $0 pump WE added. Only this script ever adds this variant.
  function isFreePumpLine(item) {
    return item.variant_id === CONFIG.freePumpVariantId;
  }

  function sum(items, fn) {
    return items.reduce(function (n, i) { return n + (fn(i) ? i.quantity : 0); }, 0);
  }

  function getDismissedAt() {
    try {
      var v = window.sessionStorage.getItem(DISMISS_KEY);
      return v === null ? -1 : parseInt(v, 10);
    } catch (e) { return -1; }
  }

  function setDismissedAt(n) {
    try {
      if (n === null) window.sessionStorage.removeItem(DISMISS_KEY);
      else window.sessionStorage.setItem(DISMISS_KEY, String(n));
    } catch (e) {}
  }

  /* ------------------------------------------------------------------- math */

  // How many paid pumps we have converted into free ones. Persisted as a CART
  // ATTRIBUTE rather than sessionStorage, because it has to survive a refresh,
  // a second tab, and a different device. If this count were ever lost while a
  // swap was in effect, the customer would silently end up with fewer pumps
  // than they originally chose to buy.
  function getSwapped(cart) {
    var attrs = (cart && cart.attributes) || {};
    var n = parseInt(attrs[SWAP_ATTR], 10);
    return (isNaN(n) || n < 0) ? 0 : n;
  }

  function plan(cart) {
    var items = cart.items || [];

    var eligibleQty = eligibleUnits(items);
    var earned = Math.floor(eligibleQty / CONFIG.threshold);
    if (CONFIG.maxFreePumps > 0) earned = Math.min(earned, CONFIG.maxFreePumps);

    var paidQty = sum(items, isPaidPumpLine);
    var freeQty = sum(items, isFreePumpLine);
    var swapped = getSwapped(cart);

    var paidLine = null, freeLine = null;
    for (var i = 0; i < items.length; i++) {
      if (!paidLine && isPaidPumpLine(items[i])) paidLine = items[i];
      if (!freeLine && isFreePumpLine(items[i])) freeLine = items[i];
    }

    // Pumps the customer actively chose = what is still on their paid line PLUS
    // any we already converted. Without that second term, dropping back below
    // the threshold would hand back fewer pumps than they originally added.
    var customerWanted = paidQty + swapped;

    var desiredFree = earned;

    // Respect a customer who deleted the free pump: stand down until they cross
    // a HIGHER threshold. With the cap at 1 there is no higher tier, so this
    // effectively means "not again this session", which is the intent.
    var dismissedAt = getDismissedAt();
    var suppressed = dismissedAt >= 0 && earned <= dismissedAt;
    if (suppressed) desiredFree = Math.min(desiredFree, freeQty);
    if (dismissedAt >= 0 && earned < dismissedAt) setDismissedAt(null);

    // Free pumps cover the customer's own pumps first, so they never pay for a
    // pump they have earned. Anything wanted beyond the earned count stays paid.
    var desiredPaid = Math.max(0, customerWanted - desiredFree);
    var desiredSwapped = customerWanted - desiredPaid;

    return {
      eligibleQty: eligibleQty,
      earned: earned,
      paidQty: paidQty,
      freeQty: freeQty,
      swapped: swapped,
      customerWanted: customerWanted,
      paidLine: paidLine,
      freeLine: freeLine,
      desiredFree: desiredFree,
      desiredPaid: desiredPaid,
      desiredSwapped: desiredSwapped,
      suppressed: suppressed,
      changed: desiredFree !== freeQty || desiredPaid !== paidQty
    };
  }

  /* ------------------------------------------------------------- cart access */

  function internalFetch(url, options) {
    options = options || {};
    options.headers = options.headers || {};
    options.headers[SENTINEL] = '1';
    return window.__freePumpNativeFetch.call(window, url, options);
  }

  function getCart() {
    return internalFetch(cartUrl('/cart.js'), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function post(path, payload) {
    return internalFetch(cartUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  function addVariant(variantId, quantity, properties) {
    return post('/cart/add.js', {
      items: [{ id: variantId, quantity: quantity, properties: properties || {} }]
    });
  }

  function setLineQuantity(lineKey, quantity) {
    return post('/cart/change.js', { id: lineKey, quantity: quantity });
  }

  function saveSwapped(n) {
    var attributes = {};
    attributes[SWAP_ATTR] = n > 0 ? String(n) : '';
    return post('/cart/update.js', { attributes: attributes });
  }

  /* ------------------------------------------------------------- UI refresh */

  // This theme dispatches no cart events and exposes no global `halo`, so the
  // only supported way to re-render is to call each custom element's own
  // update method. `add-to-cart-popup` is deliberately excluded: it reads an
  // `added-item` cookie and throws when that cookie is absent.
  function refreshUI(cart) {
    try {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.innerText = cart.item_count;
      });
    } catch (e) { log('count refresh failed', e); }

    try {
      document.querySelectorAll('quick-cart-items, quick-upsell-cart-items').forEach(function (el) {
        if (typeof el.update === 'function') el.update(cart);
      });
    } catch (e) { log('drawer refresh failed', e); }

    try {
      var cartPage = document.querySelector('cart-items');
      if (cartPage && typeof cartPage.update === 'function') cartPage.update(cart);
    } catch (e) { log('cart page refresh failed', e); }

    try {
      document.querySelectorAll('cart-shipping-message').forEach(function (el) {
        if (typeof el.calculator === 'function') el.calculator();
      });
    } catch (e) { log('shipping bar refresh failed', e); }
  }

  /* ------------------------------------------------------------- reconciler */

  // Cart writes must run one at a time — Shopify rejects concurrent cart
  // mutations, and line keys shift as lines are added or removed.
  function apply(p) {
    var chain = Promise.resolve(null);
    var touched = false;

    // Paid line first. When we are handing a paid pump BACK (threshold lost),
    // adding it before removing the free one means the customer is never
    // momentarily left with no pump at all.
    if (p.desiredPaid !== p.paidQty) {
      chain = chain.then(function () {
        touched = true;
        if (p.paidLine) return setLineQuantity(p.paidLine.key, p.desiredPaid);
        if (p.desiredPaid > 0) return addVariant(CONFIG.paidPumpVariantId, p.desiredPaid);
        return null;
      });
    }

    if (p.desiredFree !== p.freeQty) {
      chain = chain.then(function () {
        touched = true;
        if (p.freeLine) return setLineQuantity(p.freeLine.key, p.desiredFree);
        if (p.desiredFree > 0) {
          var properties = {};
          properties[CONFIG.freeFlag] = 'true';
          return addVariant(CONFIG.freePumpVariantId, p.desiredFree, properties);
        }
        return null;
      });
    }

    // Bookkeeping last, so the swap count is only persisted if the line edits
    // above actually succeeded.
    if (p.desiredSwapped !== p.swapped) {
      chain = chain.then(function () { return saveSwapped(p.desiredSwapped); });
    }

    return chain.then(function (last) { return touched ? (last || true) : null; });
  }

  function reconcile() {
    if (busy) { queued = true; return Promise.resolve(); }
    busy = true;

    return getCart()
      .then(function (cart) {
        var p = plan(cart);
        log('plan', p);

        if (!p.changed && p.desiredSwapped === p.swapped) return null;

        // Customer deleted the free pump while still qualifying. Remember it and
        // stand down instead of fighting them on every render. We deliberately
        // do NOT restore a paid pump here: deleting the gift means "I want fewer
        // pumps", and silently re-adding a $6.49 charge they did not ask for is
        // worse than leaving them one short.
        if (p.freeQty === 0 && p.desiredFree > 0 && !p.suppressed && wasRemovedByCustomer) {
          setDismissedAt(p.earned);
          return p.swapped > 0 ? saveSwapped(0) : null;
        }

        return apply(p);
      })
      .then(function (updatedCart) {
        if (!updatedCart) return;
        return getCart().then(refreshUI);
      })
      .catch(function (e) {
        log('reconcile failed', e);
      })
      .then(function () {
        // Always clear, so a removal signal can never leak into a later cycle
        // and suppress a pump the customer legitimately re-earned.
        wasRemovedByCustomer = false;
        busy = false;
        if (queued) { queued = false; schedule(); }
      });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, CONFIG.debounceMs);
  }

  /* ------------------------------------------- detecting external cart edits */

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  function looksLikeCartWrite(url) {
    return typeof url === 'string' && CART_WRITE.test(url);
  }

  // Watch for the customer zeroing our free-pump line so we can stand down.
  function noteExternalRemoval(bodyText) {
    if (!bodyText) return;
    try {
      var parsed = JSON.parse(bodyText);
      var zeroing = (parsed && parsed.quantity === 0) ||
                    (parsed && parsed.updates && Object.keys(parsed.updates).some(function (k) {
                      return parsed.updates[k] === 0;
                    }));
      if (zeroing) wasRemovedByCustomer = true;
    } catch (e) { /* form-encoded or opaque body — ignore */ }
  }

  window.__freePumpNativeFetch = window.fetch;

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isInternal = !!(init && init.headers && (init.headers[SENTINEL] || init.headers[SENTINEL.toLowerCase()]));

    var result = window.__freePumpNativeFetch.apply(this, arguments);

    if (!isInternal && looksLikeCartWrite(url)) {
      if (init && typeof init.body === 'string') noteExternalRemoval(init.body);
      result.then(function () { schedule(); }).catch(function () {});
    }
    return result;
  };

  // The theme adds to cart from product cards via jQuery $.ajax (Shopify.addItem),
  // so XHR must be wrapped too — fetch alone would miss half the add paths.
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__freePumpUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var self = this;
    if (looksLikeCartWrite(this.__freePumpUrl)) {
      if (typeof body === 'string') noteExternalRemoval(body);
      this.addEventListener('load', function () { schedule(); });
    }
    return nativeSend.apply(self, arguments);
  };

  /* ------------------------------------------------------------------- boot */

  function boot() {
    // One pass on load catches carts restored from a previous session, and
    // carts left inconsistent by an earlier failure.
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Exposed for manual testing from the console.
  window.SimpsFreePump = {
    config: CONFIG,
    reconcile: reconcile,
    plan: plan,
    inspect: function () { return getCart().then(function (c) { return plan(c); }); },
    resetDismissal: function () { setDismissedAt(null); return 'dismissal cleared'; }
  };
})();
